<template>
  <v-btn
    :loading="isConnecting"
    :color="btn_color"
    variant="flat"
    @click="click()"
  >
    <v-icon left class="mr-1">{{ icon }}</v-icon>
    {{ text }}
  </v-btn>
</template>

<script setup lang="ts">
import { inject, ref, computed } from "vue";
import { useEventBus } from "@/composables/useEventBus";
import { useModbusAddress } from "@/composables/useModbusAddress";
import { PortState } from "@/enums/PortState";
import { useModbusStore } from "@/stores/useModbusStore";
import { Logger } from "@/types/Logger";

const $log = inject<Logger>("vuejs3-logger");

const { emit } = useEventBus();
const { modbusAddress } = useModbusAddress();
const modbusStore = useModbusStore();

const clicked = ref(false);

// Filter options are not used at the moment:
// There are situations when communication with an EQ-DA is done via a hardware
// interface, for example when communication is done via a USB-to-RS485.
// Catching all these situations could be complicated and it has not been looked into.
// const EQ_DA_FILTER = { usbProductId: 0x5740, usbVendorId: 0x483 };
const filterOptions = {}; // { filters: [EQ_DA_FILTER] }

const isConnecting = computed<boolean>(() => {
  return modbusStore.portState === PortState.connecting || clicked.value;
});

const text = computed(() => {
  if (isConnecting.value) {
    return "Connecting to a device";
  }
  switch (modbusStore.portState) {
    case PortState.disconnected:
      return `Connect to device`;
    case PortState.connected:
      return `Disconnect`;
  }
});

const icon = computed(() => {
  if (isConnecting.value) {
    return "loop";
  }
  switch (modbusStore.portState) {
    case PortState.disconnected:
      return "usb";
    case PortState.connected:
      return "usb_off";
  }
});

const btn_color = computed(() => {
  if (modbusStore.portState === PortState.connected) {
    return "warning";
  } else {
    return "primary";
  }
});

function promptInt(message: string, _default: string): number {
  return parseInt(prompt(message, _default) ?? _default);
}

async function click() {
  if (isConnecting.value) {
    $log?.info("ConnectWidget: already (dis)connecting, no action");
    return;
  }
  clicked.value = true;
  const { parameterVersion } = modbusAddress;

  switch (modbusStore.portState) {
    case PortState.disconnected:
      try {
        const serialPort = await navigator.serial.requestPort(filterOptions);
        const baud = promptInt("Baud rate", "115200");
        const deviceAddress = promptInt("Modbus Device Address", "1");
        const allPorts = await navigator.serial.getPorts();
        let portIndex = -1;
        for (let i = 0; i < allPorts.length; i++) {
          if (allPorts[i] === serialPort) {
            portIndex = i;
            break;
          }
        }
        if (await modbusStore.openSerial(portIndex, baud, deviceAddress)) {
          await modbusStore.modbusRead(deviceAddress, parameterVersion, 2);
          emit("connected", null);
        }
      } catch (error) {
        $log?.error(error);
      }
      break;
    case PortState.connected:
      if (await modbusStore.closeSerial()) {
        await modbusStore.saveModbusValue([parameterVersion, 0, 0, 0, 0]);
        emit("disconnected", null);
      }
      break;
  }
  clicked.value = false;
}
</script>
